import React from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import { Link } from 'react-router-dom';
import { SEOHead, organizationSchema, mobileAppSchema, BRAND } from './SEOUtils';

export default function PolyDatingAppPage() {
  const schemas = [organizationSchema, mobileAppSchema];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="Poly Dating App for Ethical Non-Monogamy | UnityLove"
        description="UnityLove is the poly dating app for polyamory, polygamy, and ethical non-monogamy—while welcoming intentional monogamy. Black poly-centered."
        canonical="/poly-dating-app"
        schemas={schemas}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-slate-900 to-slate-800 text-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            The Poly Dating App That Gets It
          </h1>
          <p className="text-xl text-slate-300 mb-4 max-w-2xl mx-auto">
            UnityLove is a poly dating app built for polyamory, polygamy, and ethical non-monogamy.
          </p>
          <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
            We also welcome those who practice intentional monogamy within a community grounded in honesty, consent, and respect. Black poly-centered. Not an afterthought filter—a complete platform for conscious connection.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/auth?mode=signup"
              className="inline-flex items-center justify-center px-8 py-4 bg-rose-600 text-white rounded-xl font-semibold hover:bg-rose-700 transition-colors"
            >
              Start Dating Free
            </Link>
            <a
              href={BRAND.appStoreUrl}
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white rounded-xl font-semibold hover:bg-white/10 transition-colors"
            >
              Get the App
            </a>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-16">
        
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            Why Poly People Need Their Own Dating App
          </h2>
          <p className="text-lg text-slate-700 mb-4">
            Most dating apps treat poly like an edge case. A checkbox buried in settings. A filter that yields two results in your entire city. If you've tried dating as a polyamorous person on mainstream apps, you know the frustration.
          </p>
          <p className="text-lg text-slate-700 mb-4">
            UnityLove is different. We built a poly dating app where your relationship style isn't a filter—it's the foundation. Every feature considers the reality of polyamorous dating: multiple partners, complex calendars, metamour relationships, and the need for clear communication.
          </p>
          <p className="text-lg text-slate-700">
            Whether you're solo poly, part of a couple opening up, looking to grow your polycule, or practicing relationship anarchy, you'll find your people here.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            Features Built for Poly Dating
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-rose-50 to-slate-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Partner Linking</h3>
              <p className="text-slate-700">Link profiles with your existing partners so matches can see your full constellation. Transparency from the start.</p>
            </div>
            <div className="bg-gradient-to-br from-rose-50 to-slate-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Relationship Structure Tags</h3>
              <p className="text-slate-700">Hierarchical, non-hierarchical, kitchen table, parallel—tag your style and filter for compatible structures.</p>
            </div>
            <div className="bg-gradient-to-br from-rose-50 to-slate-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Couples Profiles</h3>
              <p className="text-slate-700">Dating together? Create a couples profile that represents both partners—no hacks or workarounds required.</p>
            </div>
            <div className="bg-gradient-to-br from-rose-50 to-slate-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Seeking Options</h3>
              <p className="text-slate-700">Looking for a nesting partner? A comet? A play partner? Define what you're seeking and match with aligned intentions.</p>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            Ethical Non-Monogamy Dating Done Right
          </h2>
          <p className="text-lg text-slate-700 mb-4">
            The "ethical" in ethical non-monogamy isn't just a label—it's a practice. UnityLove embeds ENM values throughout the platform:
          </p>
          <ul className="space-y-4 text-lg text-slate-700">
            <li className="flex items-start gap-3">
              <span className="text-rose-600 font-bold">•</span>
              <span><strong>Consent culture:</strong> Features designed around checking in, setting boundaries, and respecting limits.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-rose-600 font-bold">•</span>
              <span><strong>Transparency tools:</strong> Share your relationship structure, agreements, and what your partners know/consent to.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-rose-600 font-bold">•</span>
              <span><strong>Communication prompts:</strong> Conversation starters that go beyond surface chat to real ENM topics.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-rose-600 font-bold">•</span>
              <span><strong>Community standards:</strong> Clear guidelines against unicorn hunting, couple privilege behavior, and deceptive practices.</span>
            </li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            All Relationship Structures Welcome
          </h2>
          <p className="text-lg text-slate-700 mb-4">
            Poly isn't one-size-fits-all. UnityLove supports the full spectrum:
          </p>
          <div className="grid sm:grid-cols-2 gap-4 text-slate-700">
            <div className="bg-slate-50 p-4 rounded-lg">
              <strong>Polyamory</strong> — Multiple loving relationships with consent
            </div>
            <div className="bg-slate-50 p-4 rounded-lg">
              <strong>Polygamy</strong> — Structured multi-spouse family commitment
            </div>
            <div className="bg-slate-50 p-4 rounded-lg">
              <strong>Hierarchical</strong> — Primary/secondary relationship structures
            </div>
            <div className="bg-slate-50 p-4 rounded-lg">
              <strong>Non-Hierarchical</strong> — No ranking of partners
            </div>
            <div className="bg-slate-50 p-4 rounded-lg">
              <strong>Relationship Anarchy</strong> — No predefined relationship rules
            </div>
            <div className="bg-slate-50 p-4 rounded-lg">
              <strong>Intentional Monogamy</strong> — Traditional commitment with clarity
            </div>
          </div>
          <p className="text-lg text-slate-700 mt-4">
            Interested in the intersection of poly dating and culture? Explore <Link to="/black-poly-dating" className="text-rose-600 hover:text-rose-700 underline">Black poly dating</Link> on UnityLove.
          </p>
        </section>

        {/* CTA Section */}
        <section className="bg-slate-900 text-white rounded-2xl p-8 md:p-12 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready for Poly Dating That Works?</h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Join a growing community of people building intentional relationships on UnityLove.
          </p>
          <Link
            to="/auth?mode=signup"
            className="inline-flex items-center justify-center px-8 py-4 bg-rose-600 text-white rounded-xl font-semibold hover:bg-rose-700 transition-colors"
          >
            Create Your Free Profile
          </Link>
        </section>

        {/* Choose Your Structure Section */}
        <section className="mb-12 bg-rose-50 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">
            Choose the Relationship Structure That Fits Your Values
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl text-center">
              <h3 className="font-semibold text-slate-900 mb-2">Polyamory</h3>
              <p className="text-slate-600 text-sm">Multiple loving relationships practiced with honesty, consent, and open communication.</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center">
              <h3 className="font-semibold text-slate-900 mb-2">Polygamy</h3>
              <p className="text-slate-600 text-sm">Structured multi-partner or multi-spouse commitment, often rooted in family, culture, and long-term responsibility.</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center">
              <h3 className="font-semibold text-slate-900 mb-2">Intentional Monogamy</h3>
              <p className="text-slate-600 text-sm">Traditional one-partner commitment built on clarity, trust, and shared purpose.</p>
            </div>
          </div>
          <p className="text-center text-slate-700 mt-6">
            Whatever your structure, UnityLove prioritizes honesty, consent, and respect.
          </p>
        </section>

        {/* Related Pages */}
        <section className="mt-12 pt-8 border-t border-slate-200">
          <h3 className="text-xl font-semibold text-slate-900 mb-4">Related Topics</h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/black-poly-dating" className="text-rose-600 hover:text-rose-700 underline">Black Poly Dating</Link>
            <Link to="/black-polyamory-dating-app" className="text-rose-600 hover:text-rose-700 underline">Black Polyamory Dating</Link>
            <Link to="/ethical-non-monogamy-dating" className="text-rose-600 hover:text-rose-700 underline">Ethical Non-Monogamy</Link>
            <Link to="/about-unitylove" className="text-rose-600 hover:text-rose-700 underline">About UnityLove</Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-100 py-8 px-4">
        <div className="max-w-4xl mx-auto text-center text-slate-600">
          <p>© {new Date().getFullYear()} UnityLove. The poly dating app for ethical non-monogamy.</p>
        </div>
      </footer>
    </div>
  );
}
